import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useFavorites } from "../contexts/FavoritesContext";
import { Heart, LogOut, UserCog, Sparkles, Package, Flame } from "lucide-react";
import { MobileNavMenu } from "./MobileNavMenu";
import { ProfileDialog } from "./ProfileDialog";
import { getDisplayName } from "../lib/displayName";
import { api } from "../lib/api";

export function Header() {
  const { user, logout } = useAuth();
  const { favorites } = useFavorites();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const [streak, setStreak] = useState(0);

  // Idempotent daily touch — runs once per session, updates the streak counter.
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    let cancelled = false;
    const touch = async () => {
      try {
        const { data } = await api.post("/usage/touch");
        if (!cancelled) setStreak(data?.streak || 0);
      } catch {
        try {
          const { data } = await api.get("/usage/streak");
          if (!cancelled) setStreak(data?.streak || 0);
        } catch {
          // silent — streak is a nice-to-have, not load-blocking
        }
      }
    };
    touch();
    return () => { cancelled = true; };
  }, [user]);

  // React to streak changes triggered elsewhere (e.g. the Dashboard's streak-save banner).
  useEffect(() => {
    const onChange = (e) => {
      if (typeof e?.detail?.streak === "number") setStreak(e.detail.streak);
    };
    window.addEventListener("stagvault:streak-changed", onChange);
    return () => window.removeEventListener("stagvault:streak-changed", onChange);
  }, []);

  const handleLogout = async () => {
    // Full navigation avoids the ProtectedRoute race that otherwise lands signed-out users on /sign-in?next=/vault.
    try { await logout(); } catch { /* token already cleared locally */ }
    window.location.assign("/");
  };

  const onFavorites = location.pathname.startsWith("/vault/favorites");
  const onTemplates = location.pathname.startsWith("/vault/templates");
  const displayName = getDisplayName(user);

  return (
    <header
      className="sticky top-0 z-50 bg-black/85 backdrop-blur-xl border-b border-purple-500/20"
      data-testid="app-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-3 md:py-4 flex items-center justify-between gap-2 overflow-x-clip">
        <Link
          to="/vault"
          className="flex items-center group"
          data-testid="header-home-link"
        >
          <img
            src="/stag-vault-lockup-v2.webp"
            alt="Stag Vault · AI · Passive Income"
            className="h-9 sm:h-11 md:h-14 w-auto select-none transition-all duration-300 group-hover:drop-shadow-[0_0_16px_rgba(168,85,247,0.55)]"
            style={{
              filter: "drop-shadow(0 0 12px rgba(168, 85, 247, 0.35)) drop-shadow(0 0 4px rgba(176, 112, 255, 0.2))",
            }}
            draggable={false}
            data-testid="header-lockup"
          />
        </Link>

        {user && typeof user === "object" && (
          <div className="flex items-center gap-1.5 sm:gap-3 md:gap-4 min-w-0">
            {user.founding_member && (
              <span
                className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-purple-400/60 bg-purple-500/15 text-purple-200 text-[10px] tracking-[0.25em] uppercase font-mono font-bold shadow-[0_0_14px_rgba(168,85,247,0.35)]"
                title="Founding lifetime member — Elite unlocked forever, founding-member perks for life. Thank you."
                data-testid="header-founding-badge"
              >
                <Sparkles size={11} className="text-purple-300" />
                Founding
              </span>
            )}
            {user.tier !== "lifetime" && user.tier !== "monthly" && (
              <Link
                to="/pricing"
                className="hidden sm:inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-400 text-black px-3 md:px-4 py-2 text-xs tracking-[0.2em] uppercase font-bold transition-all duration-300 shadow-[0_0_14px_rgba(168,85,247,0.45)] hover:shadow-[0_0_24px_rgba(168,85,247,0.8)]"
                data-testid="header-upgrade-link"
              >
                <Sparkles size={14} />
                Upgrade
              </Link>
            )}
            {user.tier !== "lifetime" && (
              <Link to="/my-vault" className="hidden md:inline-flex min-h-11 items-center border border-zinc-800 px-3 py-2 text-xs tracking-[0.15em] uppercase text-zinc-300 hover:border-purple-500/60 hover:text-purple-300" data-testid="header-my-vault-link">My Vault</Link>
            )}
            <Link
              to="/vault/templates"
              className={`inline-flex min-h-11 min-w-11 items-center justify-center gap-2 border px-2.5 sm:px-4 py-2 text-sm tracking-wide transition-all duration-300 ${
                onTemplates
                  ? "border-purple-500 text-purple-300 bg-purple-500/10"
                  : "border-zinc-800 hover:border-purple-500/60 text-zinc-300 hover:text-purple-300"
              }`}
              data-testid="header-templates-link"
              aria-label="Templates"
            >
              <Package size={16} />
              <span className="hidden sm:inline">Templates</span>
            </Link>
            {streak > 0 && (
              <span
                className="hidden md:inline-flex items-center gap-1.5 text-amber-300/90 font-mono text-[11px] tracking-[0.18em] uppercase select-none"
                title={`${streak} day${streak === 1 ? "" : "s"} in a row using the vault`}
                data-testid="header-streak"
              >
                <Flame size={13} className="drop-shadow-[0_0_6px_rgba(176,112,255,0.45)]" strokeWidth={2.25} />
                <span>{streak}d streak</span>
              </span>
            )}
            <Link
              to="/vault/favorites"
              className={`inline-flex min-h-11 min-w-11 items-center justify-center gap-2 border px-2.5 sm:px-4 py-2 text-sm tracking-wide transition-all duration-300 ${
                onFavorites
                  ? "border-purple-500 text-purple-300 bg-purple-500/10"
                  : "border-zinc-800 hover:border-purple-500/60 text-zinc-300 hover:text-purple-300"
              }`}
              data-testid="header-favorites-link"
              aria-label={`Saved prompts (${favorites.length})`}
            >
              <Heart size={16} fill={onFavorites ? "currentColor" : "none"} />
              <span className="hidden sm:inline">Saved</span>
              <span className="text-purple-400">{favorites.length}</span>
            </Link>

            <button
              type="button"
              onClick={() => setProfileOpen(true)}
              className="hidden md:flex flex-col items-end leading-tight text-right group"
              data-testid="header-profile-button"
              title="Change how your name appears"
            >
              <span className="text-purple-400/80 text-xs tracking-[0.25em] uppercase group-hover:text-purple-300 transition-colors">
                Welcome,
              </span>
              <span
                className="text-base text-zinc-200 group-hover:text-purple-300 font-display tracking-tight transition-colors inline-flex items-center gap-1.5"
                data-testid="header-user-name"
              >
                {displayName}
                <UserCog size={12} className="opacity-50 group-hover:opacity-100 transition-opacity" />
              </span>
            </button>
            <button
              onClick={handleLogout}
              className="min-h-11 min-w-11 justify-center text-sm tracking-wide text-zinc-300 hover:text-purple-300 border border-zinc-800 hover:border-purple-500/60 px-2.5 sm:px-4 py-2 transition-all duration-300 flex items-center gap-2"
              aria-label="Sign out"
              data-testid="logout-button"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
            <MobileNavMenu user={user} onAccount={() => setProfileOpen(true)} testPrefix="app-mobile" />
          </div>
        )}
        {!user && <MobileNavMenu testPrefix="app-mobile" />}
      </div>
      <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
    </header>
  );
}
